import { m } from 'framer-motion';
import { useState, useEffect } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Card,
  Grid,
  Stack,
  Alert,
  Divider,
  Typography,
  IconButton,
  AlertTitle,
  CircularProgress,
} from '@mui/material';

import { useRouter } from 'src/routes/hooks';

import { useNetworkStatus } from 'src/hooks/use-network-status';

import { useSystemStore } from 'src/store/SystemStore';
import { SeverErrorIllustration } from 'src/assets/illustrations';
import supportPageQrCode from 'src/assets/images/qrCode-support-page.svg';

import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { varBounce, MotionContainer } from 'src/components/animate';

// Constants
const SUPPORT_URL = 'https://www.point-craft.com/support';
const SUPPORT_PHONE = '612-473-5556';
const SUPPORT_EMAIL = 'support@point-craft.com';

export default function OfflineView() {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { appVersion } = useSystemStore();
  const { isOnline, isChecking, lastOnlineTime, checkCount, checkConnection } = useNetworkStatus();

  const [autoRetryCountdown, setAutoRetryCountdown] = useState(10);

  // Auto-retry countdown
  useEffect(() => {
    if (!isOnline) {
      const interval = setInterval(() => {
        setAutoRetryCountdown((prev) => {
          if (prev <= 1) {
            return 10; // Reset countdown
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [isOnline]);

  // Handle connection restoration
  useEffect(() => {
    if (isOnline) {
      enqueueSnackbar('Internet connection restored! Redirecting to dashboard...', {
        variant: 'success',
        autoHideDuration: 2000,
      });

      setTimeout(() => {
        // router.push(paths.dashboard.root);
      }, 2000);
    }
  }, [isOnline, router, enqueueSnackbar]);

  const handleRetryConnection = async () => {
    const connected = await checkConnection();
    if (!connected) {
      enqueueSnackbar('Still unable to connect. Please check your network settings.', {
        variant: 'warning',
      });
    }
  };

  const handleSupportClick = () => {
    window.open(SUPPORT_URL, '_blank');
  };

  const formatTime = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleString();
  };

  const troubleshootingSteps = [
    'Check your WiFi or ethernet connection',
    'Restart your router or modem',
    'Disable VPN if active',
    'Check with your network administrator',
    'Contact your internet service provider',
  ];

  return (
    <Box sx={{ minWidth: '900px', px: 3 }}>
      <MotionContainer>
        <m.div variants={varBounce().in}>
          <Stack sx={{ mb: 0 }} alignItems="center">
            <Box sx={{ mb: 0 }}>
              <Iconify
                icon="mdi:wifi-off"
                sx={{
                  color: 'error.main',
                  fontSize: 80,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            </Box>
            <Typography variant="h3" color="error.main" textAlign="center">
              No Internet Connection
            </Typography>
            <Typography variant="h6" color="text.secondary" textAlign="center">
              Please check your network connection to continue using PointCraft
            </Typography>
          </Stack>
        </m.div>

        <m.div variants={varBounce().in}>
          <Grid container spacing={3} sx={{ mb: 0 }}>
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Stack spacing={3}>
                  <Stack spacing={1}>
                    <Typography variant="h6" color="primary">
                      Connection Status
                    </Typography>
                    <Divider />
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight="600">
                            Current Status
                          </Typography>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: 'error.lighter',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'error.main',
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Iconify icon="mdi:wifi-off" sx={{ color: 'error.main' }} />
                              <Typography
                                variant="h5"
                                color="error.main"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                OFFLINE
                              </Typography>
                            </Stack>
                            <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
                              No internet connection detected
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight="600">
                            Last Connected
                          </Typography>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: 'grey.100',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'grey.300',
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Iconify icon="mdi:clock-outline" sx={{ color: 'text.secondary' }} />
                              <Typography
                                variant="body1"
                                color="text.primary"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {formatTime(lastOnlineTime)}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Connection attempts: {checkCount}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Alert severity="error">
                    <AlertTitle>Internet Connection Required</AlertTitle>
                    PointCraft requires an active internet connection to function properly. Please
                    check your network settings and try again.
                  </Alert>

                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight="600">
                      Troubleshooting Steps:
                    </Typography>
                    <Box component="ol" sx={{ pl: 2, m: 0 }}>
                      {troubleshootingSteps.map((step, index) => (
                        <Typography
                          key={index}
                          component="li"
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {step}
                        </Typography>
                      ))}
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <LoadingButton
                      variant="contained"
                      size="large"
                      color="primary"
                      loading={isChecking}
                      onClick={handleRetryConnection}
                      startIcon={<Iconify icon="mdi:refresh" />}
                      sx={{ flex: 1, py: 1.5 }}
                    >
                      Try Again
                    </LoadingButton>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress
                        size={20}
                        variant="determinate"
                        value={(autoRetryCountdown / 10) * 100}
                        color="primary"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Auto-retry in {autoRetryCountdown}s
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={handleSupportClick}
                      color="primary"
                      sx={{
                        border: 1,
                        borderColor: 'primary.main',
                        '&:hover': { backgroundColor: 'primary.lighter' },
                      }}
                    >
                      <Iconify icon="mdi:help-circle" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: 'background.neutral' }}>
                <Stack spacing={2} alignItems="center">
                  <Typography variant="h6" color="primary" textAlign="center">
                    Need Help?
                  </Typography>
                  <Divider sx={{ width: '100%' }} />

                  <SeverErrorIllustration sx={{ height: 120, opacity: 0.8 }} />

                  <Stack spacing={3} sx={{ width: '100%' }}>
                    <Stack spacing={1} sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        📞 Support Phone
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontFamily: 'monospace' }}>
                        {SUPPORT_PHONE}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Press #2 for Technical Support
                      </Typography>
                    </Stack>

                    <Stack spacing={1} sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        📧 Email Support
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
                        {SUPPORT_EMAIL}
                      </Typography>
                    </Stack>

                    <Stack spacing={1} sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="600">
                        🌐 Online Support
                      </Typography>
                      <Typography
                        variant="body2"
                        color="primary"
                        component="a"
                        href={SUPPORT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        www.point-craft.com/support
                      </Typography>
                    </Stack>

                    <Stack alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" fontWeight="600" textAlign="center">
                        Scan for Quick Support
                      </Typography>
                      <Box
                        component="img"
                        src={supportPageQrCode}
                        alt="Support Page QR Code"
                        sx={{
                          width: 120,
                          height: 120,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0.5,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Mobile support portal
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </m.div>

        {/* System Information Footer */}
        <m.div variants={varBounce().in}>
          <Card sx={{ p: 2, backgroundColor: 'grey.50' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  <strong>App Version:</strong> v{appVersion.current}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> Waiting for connection...
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%, 50%': { opacity: 1 },
                      '51%, 100%': { opacity: 0.3 },
                    },
                  }}
                />
                <Typography variant="body2" color="error.main">
                  Offline
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </m.div>
      </MotionContainer>
    </Box>
  );
}
